import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TextField, Spinner, Alert, Button } from '@lyse-as/formds-react';

import { VisibilityIcon, VisibilityOffIcon } from '@lyse-as/formds-react/lib/icon/common';

import { ContainerFixed } from 'app/components/container';
import { getPin, setPin } from 'app/store/actions/pin-thunks';
import { pinChanged, setCurrentPinState } from 'app/store/actions/pin-actions';
import { FetchStatus, PinLifecycleState } from 'app/store/root-types';
import { useAppDispatch, useAppSelector } from 'app/hooks/redux-thunk';
import { Breakpoint, Typography } from 'app/component-library-wave';
import { getDataLayers } from 'app/utils/trackingService';
import { getAnonymizedCurrentPageUrl, GtmElementCta } from 'app/utils/gtm-tracking-utils';

import styles from './change-pin.module.scss';

// Rare control char used only to split the translated "current PIN" sentence around its
// interpolated {{code}} — it never appears in the visible text.
const CODE_SENTINEL = String.fromCharCode(0);
const PIN_MASK_CHAR = '•'; // bullet

export const ChangePin = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { fetchStatus, data: pinData } = useAppSelector((state) => state.pin);
  const customerId = useAppSelector((state) => state.userContext.selectedCustomerLocation?.customerCrmId);

  const { localPinChange, currentState } = useAppSelector((state) => state.pin);

  const [pinVisible, setPinVisible] = useState(false);

  // Split "Gjeldende PIN-kode: {{code}}" around the interpolated code so the code can live in its
  // own fixed-width element — this keeps the masked and revealed states the same width so the
  // visibility toggle button never shifts.
  const [currentPinPrefix, currentPinSuffix = ''] = t('pages.changePin.showPin', {
    code: CODE_SENTINEL,
  }).split(CODE_SENTINEL);

  useEffect(() => {
    if (customerId) {
      dispatch(getPin(customerId));
    }
  }, []);

  const handlePinChange = (event: ChangeEvent<HTMLInputElement>) => {
    const digits = event.target.value.replace(/\D/g, '').slice(0, 4);
    dispatch(pinChanged(digits));
  };

  const handleCancelClick = () => {
    getDataLayers().customEvents.pushButtonClickEvent({
      page_url: getAnonymizedCurrentPageUrl(),
      element_cta: GtmElementCta.PIN_NEW_PIN_CANCEL,
    });

    dispatch(setCurrentPinState(PinLifecycleState.INITIAL));
  };

  const handleNewPinClick = () => {
    getDataLayers().customEvents.pushButtonClickEvent({
      page_url: getAnonymizedCurrentPageUrl(),
      element_cta: GtmElementCta.PIN_NEW_PIN,
    });

    dispatch(setCurrentPinState(PinLifecycleState.EDITING));
  };

  const changePinForm = () => (
    <div className={styles.changePinFormContainer}>
      {currentState === PinLifecycleState.SAVING ? (
        <span className={styles.loader} style={{ display: 'inline-flex', width: '2.5rem', height: '2.5rem' }}>
          <Spinner loadingText={t('pages.changePin.title')} loadingCompleteText={t('pages.changePin.savedTitle')} />
        </span>
      ) : (
        <>
          <div className={styles.inputContainer}>
            <TextField
              name="pin"
              label={t('pages.changePin.inputLabel')}
              description={t('pages.changePin.inputHelpText')}
              autoFocus={true}
              value={localPinChange ?? ''}
              onChange={handlePinChange}
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
            />
          </div>
          <div className={styles.buttonContainer}>
            <Button onClick={handleCancelClick} variant="outlined" scheme="primary" className={styles.cancelButton}>
              {t('pages.changePin.buttonCancel')}
            </Button>
            <Button onClick={handleSaveClick} variant="filled" scheme="primary" className={styles.confirmButton}>
              {t('pages.changePin.buttonSave')}
            </Button>
          </div>
        </>
      )}
    </div>
  );

  const handleSaveClick = () => {
    if (localPinChange && localPinChange.length === 4) {
      getDataLayers().customEvents.pushButtonClickEvent({
        page_url: getAnonymizedCurrentPageUrl(),
        element_cta: GtmElementCta.PIN_NEW_PIN_SAVE,
      });

      const setPinRequest = {
        newPin: localPinChange,
      };
      dispatch(setPin(setPinRequest));
    } else {
      dispatch(setCurrentPinState(PinLifecycleState.ERROR));
    }
  };

  return (
    <ContainerFixed isNarrow={true}>
      <div className={styles.changePinContainer}>
        {fetchStatus === FetchStatus.REJECTED && (
          <Alert scheme="warning" role="alert">
            {t(`pages.product.errorMessage.getProductsFailed`)}
          </Alert>
        )}
        {fetchStatus === FetchStatus.FULFILLED && (
          <>
            <Typography
              variant="headline5"
              component="h1"
              className={styles.changePinHeadline}
              maxBreakpoint={Breakpoint.TABLET}
            >
              {currentState === PinLifecycleState.SAVED ? t('pages.changePin.savedTitle') : t('pages.changePin.title')}
            </Typography>
            {pinData?.pin && (
              <div className={styles.currentPin}>
                <Typography variant="uiText1" bold={true} component="p" maxBreakpoint={Breakpoint.TABLET}>
                  {currentPinPrefix}
                  <span className={styles.pinCode} style={{ minWidth: `${pinData.pin.length}ch` }}>
                    {pinVisible ? pinData.pin : PIN_MASK_CHAR.repeat(pinData.pin.length)}
                  </span>
                  {currentPinSuffix}
                </Typography>
                <button
                  type="button"
                  className={styles.togglePinVisibility}
                  onClick={() => setPinVisible((visible) => !visible)}
                  aria-pressed={pinVisible}
                  aria-label={t(pinVisible ? 'pages.changePin.hidePinAriaLabel' : 'pages.changePin.showPinAriaLabel', {
                    defaultValue: pinVisible ? 'Skjul PIN-kode' : 'Vis PIN-kode',
                  })}
                >
                  {pinVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </button>
              </div>
            )}
            <Typography
              variant="paragraph2"
              component="p"
              className={styles.changePinParagraph}
              maxBreakpoint={Breakpoint.TABLET}
            >
              {t('pages.changePin.body')}
            </Typography>
            {currentState === PinLifecycleState.EDITING ||
            currentState === PinLifecycleState.ERROR ||
            currentState === PinLifecycleState.SAVING ? (
              changePinForm()
            ) : (
              <>
                <Button className={styles.confirmButton} onClick={handleNewPinClick} variant="filled" scheme="primary">
                  {t('pages.changePin.buttonNew')}
                </Button>
              </>
            )}
          </>
        )}
      </div>
    </ContainerFixed>
  );
};
